import React, { ReactElement, useContext } from 'react'
import styles from '../../styles'
import { FormControl, Input, makeStyles, MenuItem, Select } from '@material-ui/core'
import { Theme } from '@material-ui/core/styles/createMuiTheme'
import { useIntl } from 'react-intl'
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { FieldRenderProps } from 'react-final-form'
import { WHITE } from '../../theme'
import { ResponsiveContext } from '../../utils/context/responsiveContext'

const useStyle = makeStyles<Theme, { isLargeScreen: boolean, shouldUppercase: boolean, fullWidth: boolean }>((theme) => ({
		...styles(theme),
		label: {
			...styles(theme).text5,
			color: theme.palette.blue.main,
			marginBottom: props => props.isLargeScreen ? 15 : 20
		},
		formControl: {
			width: props => props.fullWidth ? '100%' : 'inherit',
			padding: props => props.fullWidth ? '9px 12px 9px 20px' : 'inherit',
			boxSizing: props => props.fullWidth ? 'border-box' : 'inherit',

			'& .MuiSelect-select': {
				textTransform: props => props.shouldUppercase ? 'uppercase' : 'inherit'
			},
			'& .MuiInputBase-root.Mui-disabled': {
				opacity: 0.7,

				'& .MuiInputBase-input.Mui-disabled': {
					color: theme.palette.blue.main
				}
			}
		},
		formColorWhite: {
			backgroundColor: WHITE
		},
		menuItemRoot: {
			padding: '15px 24px',
			paddingRight: 72,
			'&$menuItemSelected, &$menuItemSelected:focus, &$menuItemSelected:hover': {
				backgroundColor: 'transparent',
				color: theme.palette.blue.main
			}
		},
		menuItemSelected: {},
		errorStyle: {
			color: '#B92D00',
			fontSize: '10px'
		},
		selectContainer: {
			display: 'flex',
			alignItems: 'flex-start',
			flexDirection: 'column',
			width: props => props.fullWidth ? '100%' : 'inherit',
			marginTop: props => props.fullWidth ? 10 : 'inherit',
		},
		textFontSize: {
			fontSize: 16,
			marginBottom: 15
		},
		placeholderStyle: {
			...styles(theme).text1,
			fontSize: 15,
			fontWeight: 500,
			opacity: 0.7,
			lineHeight: 1.33
		}
	}
))

interface AcceptingProps {
	inputTextStyle?: string,
	shouldUppercase?: boolean,
	fullWidth?: boolean,
	disabled?: boolean,
	label?: string,
	formControlStyle?: string,
	selectContainerStyle?: string
}

type FormSelectFieldProps = AcceptingProps & FieldRenderProps<string | number>

const FormSelectAcquisitionResidenceField: React.FC<FormSelectFieldProps> = (
	{
		meta: {
			touched,
			error
		},
		input,
		inputTextStyle,
		children,
		shouldUppercase = false,
		fullWidth = false,
		disabled = false,
		label,
		formControlStyle,
		selectContainerStyle
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const classes = useStyle({ isLargeScreen, shouldUppercase, fullWidth })
	const intl = useIntl()

	const formClass = classNames(
		classes.formControl,
		classes.formColorWhite,
		formControlStyle
	)

	const labelClass: ClassValue = classNames(
		classes.label,
		{
			[classes.errorStyle]: touched && error
		}
	)

	const customClass = {
		classes: {
			root: classes.menuItemRoot,
			selected: classes.menuItemSelected
		},
		ListItemClasses: {
			root: inputTextStyle
		}
	}

	const renderValueText = (element: ReactElement) => {
		const propsMessage = element.props.children.props

		if (!propsMessage || !('id' in propsMessage)) {
			return element.props.children
		}
		return intl.formatMessage({
			id: propsMessage.id,
			defaultMessage: propsMessage.defaultMessage,
			description: propsMessage.description
		})
	}

	const modifyChildren = (children: React.ReactNode): any =>
		React.Children.map(children, child => {
			if (!React.isValidElement(child)) {
				return child
			}
			let elementChild: React.ReactElement = child
			if (elementChild.type === MenuItem) {
				return React.cloneElement(elementChild, customClass)
			}
			return elementChild
		})

	return (
		<div
			className={
				classNames(
					classes.selectContainer,
					selectContainerStyle
				)
			}
		>
			{label &&
				<span
					className={labelClass}
				>
					{label}
				</span>
			}

			<FormControl className={formClass}>
				<Select
					{...input}
					displayEmpty
					disabled={disabled}
					input={<Input disableUnderline={true} className={inputTextStyle} />}
					IconComponent={ExpandMoreIcon}
					autoWidth
					renderValue={(selected) => {
						if ((selected as string[]).length === 0) {
							return <span className={classes.placeholderStyle}>{renderValueText((children as ReactElement[])[0] )}</span>;
						}
						const selectedValue = (children as ReactElement[][])[1].filter((child) => child.props.value === selected)
						if (selectedValue.length > 0) {
							return renderValueText(selectedValue[0])
						}

						return <span className={classes.placeholderStyle}>{renderValueText((children as ReactElement[])[0] )}</span>;
					}}
					MenuProps={{
						getContentAnchorEl: null,
						anchorOrigin: {
							vertical: 'top',
							horizontal: 'left'
						}
					}}
				>
					{React.Children.map(children, child => modifyChildren(child))}
				</Select>
			</FormControl>

			{
				touched &&
				((error && <span className={classes.errorStyle}>{error}</span>))
			}
		</div>
	)
}

export default FormSelectAcquisitionResidenceField
